import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const linkClass = (path: string) =>
    `text-sm font-medium transition-colors whitespace-nowrap ${
      isScrolled || menuOpen ? 'text-gray-900 hover:text-amber-800' : 'text-white hover:text-amber-200'
    } ${isActive(path) ? 'font-bold' : ''}`;

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/coaching', label: 'Coaching' },
    { to: '/faq', label: 'FAQ' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Brand */}
          <Link to="/" className="flex items-center">
            <span
              className={`text-xl sm:text-2xl font-bold tracking-wide whitespace-nowrap transition-colors ${
                isScrolled || menuOpen ? 'text-amber-900' : 'text-white'
              }`}
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              UPGRADED VERSION
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} className={linkClass(to)}>
                {label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <Link
            to="/get-started"
            className={`hidden md:inline-flex px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
              isScrolled
                ? 'bg-amber-900 text-white hover:bg-amber-800'
                : 'border-2 border-white text-white hover:bg-white hover:text-amber-900'
            }`}
          >
            Get Started
          </Link>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled || menuOpen ? 'text-gray-900' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-6 pt-2 shadow-lg">
          <div className="flex flex-col space-y-1">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`py-3 px-2 text-base font-medium border-b border-gray-100 transition-colors cursor-pointer ${
                  isActive(to) ? 'text-amber-900 font-bold' : 'text-gray-800 hover:text-amber-800'
                }`}
              >
                {label}
              </Link>
            ))}
            <Link
              to="/get-started"
              className="mt-4 w-full text-center px-6 py-3 bg-amber-900 text-white rounded-full text-sm font-medium hover:bg-amber-800 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
